// PR #974 (AI cockpit): the single shell-level executor for chat client actions.
//
// Before this hook the `/chat` page owned the router and therefore owned the
// executor. The cockpit now covers every supported route, so exactly one
// consumer must exist for the whole app: if both the page and the shell shifted
// the same queue, a single assistant action would move the browser twice (or the
// second consumer would eat the command before the visible one ran). The hook is
// mounted once in `AppLayout`, and `ChatPage` no longer touches the bus.
//
// The bus already dropped replays and duplicates; this hook adds the two checks
// that must be re-evaluated against *live* state rather than the state at
// enqueue time:
//   - the session is activated first, so a command produced under a previous
//     workspace/user is discarded instead of navigated;
//   - the live scope is re-verified before the shift, so a scope change that has
//     not fired its effect yet still fails closed.
// `resolveInternalChatActionPath` re-validates the allowlist before anything
// reaches `navigate`, so an unknown action only reports "unsupported".
//
// Two details worth naming here:
//   - the notice uses the app-level message API (`App.useApp`), because the
//     shell already renders under `<App>` and antd's static holder is both a
//     dev warning and context-blind;
//   - an action that resolves to the current URL is a true no-op: no notice and
//     no `navigate`, so no duplicate history entry is pushed.

import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App } from 'antd';

import { i18n } from '../../../i18n/index.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  LOCAL_CHAT_CLIENT_ACTION_EVENT_ID_PREFIX,
  resolveInternalChatActionPath,
  sameChatClientActionScope,
  useChatClientActionBus,
  type ChatClientActionScopeIdentity,
} from '../lib/chatClientActionBus.js';

export function useChatClientActionExecutor(): void {
  const scope = useCurrentScope();
  const navigate = useNavigate();
  const location = useLocation();
  // Notices go through the app-level message API rather than antd's static
  // `message.info`: the shell already renders under `<App>` (see `App.tsx`), so
  // the static holder only produced a dev-time "static function can not consume
  // context" warning and ignored the app's theme/prefix. antd memoises the API
  // (`useInternalMessage`), so it is a stable effect dependency.
  const { message } = App.useApp();
  // The URL the person is looking at right now: a queued action that resolves to
  // exactly this target has nothing to do (see the no-op branch below).
  const currentUrl = `${location.pathname}${location.search}`;

  const liveScope = useMemo<ChatClientActionScopeIdentity | null>(
    () => (scope === null ? null : { workspaceId: scope.workspaceId, userId: scope.userId }),
    [scope],
  );

  // A signed-out session has no active scope at all, which clears the queue and
  // the dedup memory for the next session.
  const activateScope = useChatClientActionBus((state) => state.activateScope);
  useEffect(() => {
    activateScope(liveScope);
  }, [activateScope, liveScope]);

  const pendingClientAction = useChatClientActionBus((state) => state.queue[0]);
  const shiftClientAction = useChatClientActionBus((state) => state.shift);
  useEffect(() => {
    if (pendingClientAction === undefined) return;
    if (liveScope === null || !sameChatClientActionScope(pendingClientAction.scope, liveScope)) {
      // The scope moved on between the enqueue and this effect: drop the stale
      // command instead of moving the new session's browser.
      shiftClientAction(liveScope);
      return;
    }
    const action = shiftClientAction(liveScope);
    if (action === undefined) return;
    const path = resolveInternalChatActionPath(action.action);
    if (path === undefined) {
      void message.info(i18n.t('crm.ai_chat.client_action.unsupported'));
      return;
    }
    // The target is already on screen, so there is no move to make and nothing
    // to announce: a chip such as "To deals" pressed on the deals list, or an
    // assistant move to the route the person is already on. Navigating anyway
    // would push a duplicate history entry, which makes the back button look
    // broken, so this stays a pure no-op.
    if (path === currentUrl) return;
    // The copy tells an explicit cockpit chip (a local `local:` id) apart from a
    // move the assistant proposed, even though both take the same code path.
    const isLocalChip = action.eventId.startsWith(LOCAL_CHAT_CLIENT_ACTION_EVENT_ID_PREFIX);
    void message.info(
      i18n.t(
        isLocalChip
          ? 'crm.ai_chat.cockpit.local_action_applied'
          : 'crm.ai_chat.client_action.applied',
      ),
    );
    navigate(path);
  }, [currentUrl, liveScope, message, navigate, pendingClientAction, shiftClientAction]);
}
